document.addEventListener('DOMContentLoaded', () => {
  const roots = document.querySelectorAll('.subscription-widget-root');
  if (!roots.length) return;

  roots.forEach(initSubscriptionWidget);
});

/* ── Discount table ───────────────────────────────────────────────────────
 * Read from data-discount-tiers (Theme Settings → Discount Tiers), the same
 * table the two Skio widgets use. The rows below are only the safety net for a
 * shop that has not filled the settings in, so they mirror the configured
 * table: subscriptions discount by quantity, one-time purchases never do.
 */
const DISCOUNT_TABLE_FALLBACK = [
  { bags: 1, percent: 0, onetime: 0 }, // a single unit gets no discount either way
  { bags: 2, percent: 20, onetime: 0 },
  { bags: 3, percent: 30, onetime: 0 },
  { bags: 6, percent: 35, onetime: 0 },
  { bags: 9, percent: 40, onetime: 0 }
];

function swV2ParseDiscountTiers(raw) {
  const config = { rows: DISCOUNT_TABLE_FALLBACK };
  if (!raw) return config;
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (e) {
    console.error('Subscription widget: invalid data-discount-tiers', e);
    return config;
  }
  if (Array.isArray(parsed.rows)) {
    const rows = parsed.rows
      .map((row) => ({
        bags: parseInt(row.bags, 10),
        percent: parseFloat(row.percent) || 0,
        onetime: parseFloat(row.onetime) || 0,
      }))
      .filter((row) => !isNaN(row.bags) && row.bags > 0)
      .sort((a, b) => a.bags - b.bags);
    if (rows.length) config.rows = rows;
  }
  return config;
}

// Highest row the quantity reaches. Rows are thresholds, so a quantity of 4
// against rows 1/2/3/6/9 takes row 3's discount.
function getDiscount(rows, qty, isSubscription) {
  const quantity = Math.max(parseInt(qty, 10) || 1, 1);
  let row = null;
  (rows || DISCOUNT_TABLE_FALLBACK).forEach((candidate) => {
    if (quantity >= candidate.bags) row = candidate;
  });
  if (!row) return 0;
  return isSubscription ? row.percent : row.onetime;
}

function getMaxDiscount(rows) {
  return (rows || DISCOUNT_TABLE_FALLBACK).reduce((max, row) => Math.max(max, row.percent), 0);
}

// Placeholder a merchant can drop into the widget heading to print the deepest
// subscription discount in the table. Fixed syntax, deliberately not a setting.
const MAX_DISCOUNT_TOKEN = '{max}';

// Badge copy. The two option badges used to be hardcoded ("X% off" / "Best
// Value"), so a merchant could recolor the Skio widgets' badges but could not
// retitle these. Both now come from the block; {discount} prints the live rate.
const DISCOUNT_BADGE_TOKEN = '{discount}';
const BADGE_TEXT_FALLBACK = { discount: '{discount}% off', bestValue: 'Best Value' };

function swV2BadgeLabels(root) {
  return {
    discount: root.getAttribute('data-discount-badge-text') || BADGE_TEXT_FALLBACK.discount,
    bestValue: root.getAttribute('data-best-value-badge-text') || BADGE_TEXT_FALLBACK.bestValue,
  };
}

function swV2DiscountBadgeText(template, percent) {
  const text = template || BADGE_TEXT_FALLBACK.discount;
  return text.split(DISCOUNT_BADGE_TOKEN).join(String(Math.round(percent)));
}

// ── Global selling plan settings (Theme Settings → Skio Selling Plans) ─
// Accepts a raw ID ("1234567890") or a GID ("gid://shopify/SellingPlan/1234567890")
function swV2NormalizePlanId(value) {
  const matches = String(value || '').match(/\d+/g);
  return matches && matches.length ? matches[matches.length - 1] : null;
}

// Parses data-skio-plans into {single: {30: id, …}, multi: {…}}; null when unconfigured
function swV2ParsePlanSettings(raw) {
  if (!raw) return null;
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (e) {
    console.error('Subscription widget: invalid data-skio-plans', e);
    return null;
  }
  const result = { single: {}, multi: {} };
  let hasAny = false;
  ['single', 'multi'].forEach((set) => {
    ['30', '60', '90'].forEach((freq) => {
      const id = swV2NormalizePlanId(parsed && parsed[set] ? parsed[set][freq] : null);
      if (id) {
        result[set][freq] = id;
        hasAny = true;
      }
    });
  });
  return hasAny ? result : null;
}

// This widget has no frequency selector: always the 30-day plan.
// Qty 1 → single-bag plan, qty 2+ → 2+ bags plan.
function swV2ResolvePlanId(planSettings, qty, fallbackId) {
  if (!planSettings) return fallbackId;
  const set = qty >= 2 ? planSettings.multi : planSettings.single;
  return set['30'] || set['60'] || set['90'] || fallbackId;
}

function updateButtonVisibility(root, isAvailable) {
  if (isAvailable) {
    root.classList.add('in-stock');
    root.classList.remove('out-of-stock');
  } else {
    root.classList.add('out-of-stock');
    root.classList.remove('in-stock');
  }
}

function initSubscriptionWidget(root) {
  const data = root.getAttribute('data-widget-json');
  if (!data) return;

  let parsed;
  try {
    parsed = JSON.parse(data);
  } catch (e) {
    console.error('Invalid widget JSON', e);
    return;
  }

  const { product, selling_plan_groups, selected_variant_id, has_previous_subscription } = parsed;

  const hasPreviousSubscription = has_previous_subscription === true;
  const planSettings = swV2ParsePlanSettings(root.getAttribute('data-skio-plans'));
  const discountConfig = swV2ParseDiscountTiers(root.getAttribute('data-discount-tiers'));
  const badgeLabels = swV2BadgeLabels(root);
  const discountRows = discountConfig.rows;

  const optionsContainer = root.querySelector('.subscription-widget__options');
  const addBtn = root.querySelector('.subscription-widget__add-btn');
  const messageEl = root.querySelector('.subscription-widget__message');
  const qtyInput = root.querySelector('input[name="quantity"]');


  if (!product || !product.variants || !product.variants.length) return;

  let selectedVariant;
  if (selected_variant_id) {
    selectedVariant = product.variants.find((v) => v.id === selected_variant_id);
  }
  if (!selectedVariant) {
    selectedVariant = product.variants.find((v) => v.available) || product.variants[0];
  }

  let selectedVariantId = selectedVariant.id;
  let selectedSellingPlanId = null;

  updateButtonVisibility(root, selectedVariant.available);

  if (addBtn) {
    const btnText = addBtn.querySelector('span');
    if (selectedVariant.available) {
      addBtn.disabled = false;
      if (btnText) btnText.textContent = 'Add to cart';
    } else {
      addBtn.disabled = true;
      if (btnText) btnText.textContent = 'Sold out';
    }
  }

  if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
    console.log('Subscription widget: Subscribing to variantChange for product', product.id);

    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      console.log('Subscription widget: Received variantChange event', event);

      if (!event.data || !event.data.variant) {
        console.log('Subscription widget: No variant in event data');
        return;
      }

      const variant = event.data.variant;
      console.log('Subscription widget: Variant change - ID:', variant.id, 'Available:', variant.available);

      if (variant.id) {
        selectedVariantId = variant.id;
        createOptions();

        updateButtonVisibility(root, variant.available);

        if (addBtn) {
          const btnText = addBtn.querySelector('span');
          if (variant.available) {
            addBtn.disabled = false;
            if (btnText) btnText.textContent = 'Add to cart';
          } else {
            addBtn.disabled = true;
            if (btnText) btnText.textContent = 'Sold out';
          }
        }

        if (messageEl) {
          messageEl.textContent = '';
        }
      }
    });
  } else {
    console.warn('Subscription widget: PubSub not available - subscribe or PUB_SUB_EVENTS is undefined');
  }

  const sellingPlanGroup = selling_plan_groups && selling_plan_groups.length ? selling_plan_groups[0] : null;

  const createOptions = (quantity = 1, preservedPlanId = undefined) => {
    optionsContainer.innerHTML = '';
    const isFirstLoad = preservedPlanId === undefined;
    let hasSubscriptions = false;
    
    if (sellingPlanGroup) {
      const plans = sellingPlanGroup.selling_plans || [];

      if (plans.length > 0) {
        hasSubscriptions = true;

        let bestValueIndex = -1;
        let highestDiscount = 0;

        plans.forEach((plan, index) => {
          if (index > 0) return;
          const priceAdjustments = plan.price_adjustments || [];
          if (priceAdjustments.length > 0) {
            const adjustment = priceAdjustments[0];
            const discount = adjustment.value || 0;
            if (discount > highestDiscount) {
              highestDiscount = discount;
              bestValueIndex = index;
            }
          }
        });

        plans.forEach((plan, index) => {
          if (index > 0) return;
          const isChecked = isFirstLoad ? index === 0 : String(preservedPlanId) === String(plan.id);
          const option = createSubscriptionOption(
            plan,
            selectedVariant,
            isChecked,
            index === bestValueIndex,
            hasPreviousSubscription,
            quantity,
            discountConfig,
            badgeLabels
          );
          optionsContainer.appendChild(option);

          if (isChecked) {
            selectedSellingPlanId = plan.id;
          }
        });
      }
    }

    const onetimeIsSelected = isFirstLoad ? !hasSubscriptions : preservedPlanId === null;
    const onetimeOption = createOnetimeOption(
      selectedVariant,
      onetimeIsSelected,
      quantity,
      discountRows,
      badgeLabels
    );
    optionsContainer.appendChild(onetimeOption);

    if (!hasSubscriptions || onetimeIsSelected) {
      selectedSellingPlanId = null;
    }

    
    const radioInputs = optionsContainer.querySelectorAll('input[type="radio"]');
    radioInputs.forEach((input) => {
      input.addEventListener('change', (e) => {
        if (e.target.checked) {
          const planId = e.target.value;
          selectedSellingPlanId = planId === 'onetime' ? null : planId;

          const allOptions = optionsContainer.querySelectorAll('.subscription-option');
          allOptions.forEach((opt) => opt.classList.remove('selected'));
          e.target.closest('.subscription-option').classList.add('selected');
        }
      });
    });
  }

  if (optionsContainer) {
  
    createOptions();

    const refreshOptions = () => {
      const currentQty = qtyInput ? parseInt(qtyInput?.value) : 1;
      createOptions(currentQty, selectedSellingPlanId);
    };

    if (qtyInput) {
      qtyInput.addEventListener('change', refreshOptions);
    }
  }

  addBtn.addEventListener('click', () => {
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const btnText = addBtn.querySelector('span');
    const originalBtnText = btnText ? btnText.textContent : 'Add to cart';

    addBtn.disabled = true;
    if (btnText) {
      btnText.textContent = 'Adding...';
    }

    const quantity = parseInt(qtyInput?.value) || 1;

    const formData = new FormData();
    formData.append('id', selectedVariantId);
    formData.append('quantity', quantity);

    if (selectedSellingPlanId) {
      // Plan set follows the current quantity: 1 bag → single plan, 2+ → 2+ plan
      formData.append('selling_plan', swV2ResolvePlanId(planSettings, quantity, selectedSellingPlanId));
    }

    if (cart) {
      formData.append(
        'sections',
        cart.getSectionsToRender().map((section) => section.id)
      );
      formData.append('sections_url', window.location.pathname);
      cart.setActiveElement(document.activeElement);
    }

    const config = {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json',
      },
      body: formData,
    };

    fetch(routes.cart_add_url, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          if (btnText) {
            btnText.textContent = 'Error - Try again';
          }
          addBtn.disabled = false;

          setTimeout(() => {
            if (btnText) btnText.textContent = originalBtnText;
          }, 3000);

          publish(PUB_SUB_EVENTS.cartError, {
            source: 'subscription-widget',
            productVariantId: selectedVariantId,
            errors: response.errors || response.description,
            message: response.message,
          });
          return;
        } else if (!cart) {
          window.location = routes.cart_url;
          return;
        }

        if (btnText) {
          btnText.textContent = 'Added!';
        }

        setTimeout(() => {
          addBtn.disabled = false;
          if (btnText) btnText.textContent = originalBtnText;
        }, 2000);

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'subscription-widget',
          productVariantId: selectedVariantId,
          cartData: response,
        });

        if (response.sections) {
          if (cart.classList.contains('is-empty')) {
            cart.classList.remove('is-empty');
          }

          cart.renderContents(response);
        } else {
          console.warn('Subscription widget: No sections in response, reloading page');
          window.location.reload();
        }
      })
      .catch((err) => {
        console.error('Subscription widget: Error adding to cart:', err);
        if (btnText) {
          btnText.textContent = 'Error - Try again';
        }
        addBtn.disabled = false;

        setTimeout(() => {
          if (btnText) btnText.textContent = originalBtnText;
        }, 3000);
      });
  });
}

function createOnetimeOption(
  variant,
  isSelected,
  quantity = 1,
  discountRows = DISCOUNT_TABLE_FALLBACK,
  badgeLabels = BADGE_TEXT_FALLBACK
) {
  const wrapper = document.createElement('label');
  wrapper.className = 'subscription-option' + (isSelected ? ' selected' : '');

  const radio = document.createElement('input');
  radio.type = 'radio';
  radio.name = 'subscription_plan';
  radio.value = 'onetime';
  radio.checked = isSelected;

  const discountPercent = getDiscount(discountRows, quantity, false);
  const originalPrice = variant.price / 100;
  const finalPrice = originalPrice * (1 - discountPercent / 100);

  const content = document.createElement('div');
  content.className = 'subscription-option__content';

  content.innerHTML = `
    <div class="subscription-option__left">
      <div class="subscription-option__title">One-time Purchase</div>
      ${discountPercent > 0 ? `<div class="subscription-option__badge">${swV2DiscountBadgeText(badgeLabels.discount, discountPercent)}</div>` : ''}
    </div>
    <div class="subscription-option__right">
      <div class="subscription-option__price">$${finalPrice.toFixed(2)}</div>
    </div>
  `;

  wrapper.appendChild(radio);
  wrapper.appendChild(content);

  return wrapper;
}

function createSubscriptionOption(
  plan,
  variant,
  isFirst,
  isBestValue,
  hasPreviousSubscription,
  quantity = 1,
  discountConfig = { rows: DISCOUNT_TABLE_FALLBACK },
  badgeLabels = BADGE_TEXT_FALLBACK
) {
  const header = document.querySelector('.homepage-subscription-widget__heading');
  const wrapper = document.createElement('label');
  wrapper.className = 'subscription-option' + (isFirst ? ' selected' : '');

  const radio = document.createElement('input');
  radio.type = 'radio';
  radio.name = 'subscription_plan';
  radio.value = plan.id;
  radio.checked = isFirst;

  const discountPercent = getDiscount(discountConfig.rows, quantity, true);
  const originalPrice = variant.price / 100;
  const discountedPrice = originalPrice * (1 - discountPercent / 100);

  let displayDiscountPercent = 0;

  /*if (priceAdjustments.length > 0) {
    const adjustment = priceAdjustments[0];
    if (adjustment.value_type === 'percentage') {
      discountPercent = adjustment.value;
      discountedPrice = originalPrice * (1 - discountPercent / 100);
      const roundedDiscount = Math.round(discountPercent);
      if (hasPreviousSubscription && roundedDiscount === 30) {
        displayDiscountPercent = 20;
      } else {
        displayDiscountPercent = discountPercent;
      }
    } else if (adjustment.value_type === 'fixed_amount') {
      const discountAmount = adjustment.value / 100;
      discountedPrice = originalPrice - discountAmount;
      discountPercent = ((originalPrice - discountedPrice) / originalPrice) * 100;
      displayDiscountPercent = discountPercent;
    } else if (adjustment.value_type === 'price') {
      discountedPrice = adjustment.value / 100;
      discountPercent = ((originalPrice - discountedPrice) / originalPrice) * 100;
      displayDiscountPercent = discountPercent;
    }
  }*/

  const planName = plan.name || 'Subscription';
  const servingsPerMonth = 10;
  const pricePerServing = discountedPrice / servingsPerMonth;

  const content = document.createElement('div');
  content.className = 'subscription-option__content';

  content.innerHTML = `
    <div class="subscription-option__left">
      <div class="subscription-option__title">${planName}</div>
      <div class="subscription-option__details">${servingsPerMonth} servings/mo</div>
    </div>
    <div class="subscription-option__right">
      <div class="subscription-option__price">$${discountedPrice.toFixed(2)}/mo</div>
      <div class="subscription-option__price-details">
        <span class="subscription-option__serving-info">$${pricePerServing.toFixed(2)}/serving</span>
      </div>
    </div>
  `;

  if (discountPercent > 0) {
    const discountBadge = document.createElement('div');
    discountBadge.className = 'subscription-option__badge';
    discountBadge.textContent = swV2DiscountBadgeText(badgeLabels.discount, discountPercent);
    wrapper.appendChild(discountBadge);
  }

  if (isBestValue && discountPercent > 0) {
    const bestValueBadge = document.createElement('div');
    bestValueBadge.className = 'subscription-option__badge subscription-option__badge--secondary';
    bestValueBadge.textContent = badgeLabels.bestValue;
    wrapper.appendChild(bestValueBadge);
  }

  // The heading is the block's own text (schema: "Heading"). It used to be
  // overwritten here with a literal "Subscribe and save up to 40%", which meant
  // the setting was decorative and the 40% could drift from the real table.
  // Now the merchant can drop the {max} token in and get the live top rate.
  if (header && header.textContent.includes(MAX_DISCOUNT_TOKEN)) {
    header.textContent = header.textContent
      .split(MAX_DISCOUNT_TOKEN)
      .join(String(Math.round(getMaxDiscount(discountConfig.rows))));
  }


  wrapper.appendChild(radio);
  wrapper.appendChild(content);

  return wrapper;
}
